import React, { useEffect, useState, lazy, Component, Suspense } from "react";

import "./App.css";
import {
  FORM_STATE_MODE,
  FORM_STATE_LOADING,
  FORM_STATE_SUBMITTED,
  FORM_STATE_ERROR,
  POLYTODO_BASE_URL,
  ORCHARD_LABEL,
  FARM_LABEL
} from "./constants";
import Loading from "./components/Loading";

const HtmlForm = lazy(() => import("./components/HtmlForm"));

const Submitted = lazy(() => import("./components/Submitted"));

const Error = lazy(() => import("./components/Error"));

function App() {
  const [draftOnlyPrice, setDraftOnlyPrice] = useState(false);
  const [droneAndDraftPrice, setDroneAndDraftPrice] = useState(false);
  const [droneOnlyPrice, setDroneOnlyPrice] = useState(false);
  const [orchardSurveyPrice, setOrchardSurveyPrice] = useState(false);
  const [tenderSurveyPrice, setTenderSurveyPrice] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);

  const [formData, setFormData] = useState({
    previouslySurveyed: "",
    propertyCategory: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    companyName: "",
    propertyName: "",
    estimatedArea: 0,
    estimatedFeatures: "",
    address: "",
    jobInstructions: "",
    jobType: "",
    propertyType: "",
    propertyId: "",
    propertyIdKnown: null,
    farmName: "",
    addressSearch: "",
    hectares: "",
    paddocks: "",
    notes: ""
  });

  const [formMode, setFormMode] = useState(FORM_STATE_MODE);

  const renderLoader = () => {
    return <Loading loadingMessage={""} />;
  };

  useEffect(() => {
    if (
      [FORM_STATE_LOADING, FORM_STATE_SUBMITTED, FORM_STATE_ERROR].includes(
        formMode
      )
    ) {
      window.scrollTo(0, 0);
    }
  }, [formMode]);

  const processFormData = (formData) => {
    const processedData = {
      deal: {
        use_quoted_amount_for_invoice: "No",
        dealstage: 172692307,
        sales_person: "Quote Form",
        dealname: `${formData.companyName} - ${formData.jobType}`,
        referral_type: "Website",
        client: "Private",
        priority: "Medium",
        pipeline: 14342936,
        job_instructions: formData.jobInstructions,
        property_full_address: formData.address,
        job_type: formData.jobType,
        estimated_area: Number(formData.estimatedArea),
        estimated_features: formData.estimatedFeatures
          ? Number(formData.estimatedFeatures)
          : Math.ceil(formData.estimatedArea / 3.4),
      },
      contact: {
        lastname: formData.lastName,
        firstname: formData.firstName,
        phone: formData.phone,
        email: formData.email,
      },
      company: {
        name: formData.companyName,
        full_address: formData.address,
      },
      property: {
        name: formData.propertyName,
        full_address: formData.address,
        property_type: formData.propertyCategory
      },
    };

    

    if (formData.propertyCategory === "Farm") {
      processedData.property.type_of_farm = formData.propertyType;
    } else if (formData.propertyCategory === "Orchard") {
      processedData.property.type_of_orchard = formData.propertyType;
        if (formData.propertyIdKnown) {
        processedData.property.kpin = Number(formData.propertyId);
        processedData.property.has_kpin = true;
      } else {
        processedData.property.has_kpin = false;
      }
    }

    return processedData;
  };

  const handleSubmit = async () => {
    setFormMode(FORM_STATE_LOADING);

    const processedData = processFormData(formData);

    const response = await fetch(`${POLYTODO_BASE_URL}/hubspot/quoteV2`, {
      method: "POST",
      body: JSON.stringify(processedData)
    });

    const result = await response.json();

    if (result.error) {
      setErrorMessage(result.message);
      setFormMode(FORM_STATE_ERROR);
      return null;
    }

    pollJob(result);
  };

  const pollJob = async (jobId) => {
    const response = await fetch(`${POLYTODO_BASE_URL}/hubspot/quoteV2/poll`, {
      method: "POST",
      body: JSON.stringify(jobId)
    });
    const result = await response.json();

    if (result.status === "processing") {
      setTimeout(() => pollJob(jobId), 3000);
    } else {
      processJobResult(result);
    }
  };

  const processJobResult = (result) => {
    if (result.status === "error") {
      setErrorMessage(result.errorMessage);
      setFormMode(FORM_STATE_ERROR);
    } else if (result.status === "successful") {
      const pricingSetters = {
        "Drone and Draft": setDroneAndDraftPrice,
        "Draft Only": setDraftOnlyPrice,
        "Drone Only": setDroneOnlyPrice,
        "Orchard Survey": setOrchardSurveyPrice,
        "Tender Survey": setTenderSurveyPrice
      };

      Object.entries(result.pricing).forEach(([key, price]) => {
        const setter = pricingSetters[key];
        if (setter && price > 0) {
          setter(price);
        }
      });

      if (Object.values(result.pricing).some((price) => price > 0)) {
        setFormMode(FORM_STATE_SUBMITTED);
        window.scrollTo(0, 0);
        // we can't control the scroll of the parent window, but we can update the hash which will cause the parent to scroll to the element with id "main-content"
        window.parent.location.hash = "#main-content";
      }
    }
  };

  const getProps = () => {
    return {
      formData,
      draftOnlyPrice,
      droneAndDraftPrice,
      droneOnlyPrice,
      orchardSurveyPrice,
      tenderSurveyPrice,
      errorMessage,
      setFormMode,
      setFormData,
      handleSubmit
    };
  };

  const getFormComponent = () => {
    switch (formMode) {
      case FORM_STATE_MODE:
        return <HtmlForm props={getProps()} />;
      case FORM_STATE_LOADING:
        return (
          <Loading
            loadingMessage={
              "Calculating your estimate, please do not close this window"
            }
          />
        );
      case FORM_STATE_SUBMITTED:
        return <Submitted props={getProps()} />;
      case FORM_STATE_ERROR:
        return <Error props={getProps()} />;
      default:
        return <HtmlForm props={getProps()} />;
    }
  };

  return <Suspense fallback={renderLoader()}>{getFormComponent()}</Suspense>;
}

export default App;
